// src/components/GleichstandPanel.jsx
//
// Warnung auf der Startseite der Siegerehrung: Mehrere Teams sind punktgleich
// UND haben an allen Stationen gleich viele 1., 2., 3. Plaetze - die Rangliste
// kann sie nicht trennen, aber es gibt den Preis dieses Platzes nur einmal.
// Das Orga-Team legt hier VOR dem Start fest, wer ihn bekommt. Solange etwas
// offen ist, laesst sich die Siegerehrung nicht starten.
//
// Wie beim DoppeltrefferPanel sind die Details (Teamnamen) standardmaessig
// eingeklappt und in der Beamer-Ansicht gar nicht erreichbar. Die Logik steckt
// in utils/preisvergabe.js.
import { AlertTriangle, CheckCircle2, EyeOff, RotateCcw, ChevronDown, ChevronUp } from "lucide-react";
import { slotBeschreibung } from "../utils/preisvergabe";

export default function GleichstandPanel({
  verteilung,
  aufgeklappt,
  onUmschalten,
  onWahl,
  onZuruecksetzen,
  detailsErlaubt = true,
}) {
  const offen = verteilung.gleichstandOffen;
  const entschieden = verteilung.gleichstandEntschieden;
  if (!offen && entschieden.length === 0) return null;

  return (
    <div
      className={`w-full max-w-2xl text-left rounded-lg border mb-8 ${
        offen ? "border-red-700 bg-red-900/20" : "border-green-700 bg-green-900/20"
      }`}
    >
      <div className="flex items-start gap-3 p-4">
        {offen ? (
          <AlertTriangle className="w-6 h-6 text-red-400 shrink-0 mt-0.5" />
        ) : (
          <CheckCircle2 className="w-6 h-6 text-green-400 shrink-0 mt-0.5" />
        )}
        <div className="flex-1">
          {offen ? (
            <>
              <p className="font-bold text-red-200">Gleichstand – Entscheidung des Orga-Teams nötig</p>
              <p className="text-sm text-red-100/80 mt-1">
                Mindestens zwei Teams sind punktgleich und auch bei den Stationsplätzen nicht zu
                trennen, der Preis auf diesem Platz ist aber nur einmal da. Das Orga-Team muss vor
                dem Start festlegen, wer ihn bekommt – das andere Team rückt einen Platz nach.
                Vorher lässt sich die Siegerehrung nicht starten.
              </p>
            </>
          ) : (
            <>
              <p className="font-bold text-green-200">Gleichstand entschieden</p>
              <p className="text-sm text-green-100/80 mt-1">
                {entschieden.length === 1
                  ? "1 Gleichstand wurde vom Orga-Team entschieden."
                  : `${entschieden.length} Gleichstände wurden vom Orga-Team entschieden.`}{" "}
                Die Siegerehrung kann starten.
              </p>
            </>
          )}
          {detailsErlaubt ? (
            <button
              type="button"
              onClick={onUmschalten}
              className="mt-3 inline-flex items-center gap-1 text-sm font-semibold text-primary-400 hover:text-primary-300"
            >
              {aufgeklappt ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
              {aufgeklappt ? "Details ausblenden" : offen ? "Jetzt entscheiden" : "Entscheidungen anzeigen"}
            </button>
          ) : (
            <p className="mt-3 flex items-center gap-1.5 text-sm text-gray-400">
              <EyeOff className="w-4 h-4 shrink-0" />
              {offen
                ? "Zum Entscheiden oben die Anzeige „Nur Orga-Team“ wählen."
                : "Details nur in der Anzeige „Nur Orga-Team“."}
            </p>
          )}
        </div>
      </div>

      {aufgeklappt && detailsErlaubt && (
        <div className="border-t border-secondary-800 bg-secondary-900 rounded-b-lg p-4">
          <p className="flex items-center gap-1.5 text-xs text-red-300 mb-4">
            <EyeOff className="w-3.5 h-3.5 shrink-0" />
            Nur für das Orga-Team – nicht auf dem Beamer zeigen, sonst sind die Plätze verraten.
          </p>

          {offen && (
            <>
              <p className="mb-1 text-gray-300">
                Diese {offen.kandidaten.length} Teams teilen sich den {offen.rang}. Platz (
                {offen.liste === "gesamt" ? "gesamt" : offen.liste.replace("typ:", "")}). Wer
                bekommt {offen.preise.length === 1 ? "den Preis" : "die Preise"}:{" "}
                {offen.preise
                  .map((s) => `„${s.preis.title}“ (${slotBeschreibung(s)})`)
                  .join(", ")}
                ?
              </p>
              <div className="space-y-3 mt-3">
                {offen.kandidaten.map((t) => (
                  <button
                    key={t.id ?? t.team_id ?? t.team_name}
                    type="button"
                    onClick={() => onWahl(offen.schluessel, offen.keys[offen.kandidaten.indexOf(t)])}
                    className="w-full text-left rounded-lg border border-secondary-700 bg-secondary-950 hover:border-primary-500 hover:bg-secondary-800 transition-colors p-4 text-lg font-bold text-primary-300"
                  >
                    {t.team_name}
                  </button>
                ))}
              </div>
            </>
          )}

          {entschieden.length > 0 && (
            <div className={offen ? "mt-5 border-t border-secondary-800 pt-4" : ""}>
              <div className="flex items-center justify-between mb-2">
                <p className="text-sm font-semibold text-gray-400">Bereits entschieden</p>
                <button
                  type="button"
                  onClick={onZuruecksetzen}
                  className="flex items-center gap-1 text-sm text-gray-400 hover:text-white"
                >
                  <RotateCcw className="w-3.5 h-3.5" /> Neu entscheiden
                </button>
              </div>
              <ul className="space-y-1 text-sm">
                {entschieden.map((e) => (
                  <li key={`${e.liste}-${e.rang}`}>
                    <span className="text-primary-300">{e.team.team_name}</span> bekommt den{" "}
                    {e.rang}. Platz
                    <span className="text-gray-500">
                      {" "}
                      (gleichauf mit{" "}
                      {e.kandidaten
                        .filter((k) => k !== e.team)
                        .map((k) => k.team_name)
                        .join(", ")}
                      )
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
